import { useThemeHelper } from "@/theme/themeHelper"
import { loadScript } from "@/helpers/loadScript"
import { ServiceStatus } from "@/services/ServiceStatus"
import {
  CheckoutWebComponentsFunction,
  CheckoutWebComponentsInstance,
  FlowComponent,
  FlowComponentError,
  FlowLinkInfo,
  FlowPaymentSession,
} from "@/types/CheckoutFlow"
import { PaymentSessionResponse } from "@/api/backofficeRest/generated/model"
import { isProduction } from "@/helpers/envHelper"

// PCI requirement: always load directly from checkout.com, never bundle.
const CHECKOUT_FLOW_SCRIPT_URL =
  "https://checkout-web-components.checkout.com/index.js"

export type PaymentSessionResult = PaymentSessionResponse

/**
 * Maps the backoffice gateway's camelCase payment session response to the
 * snake_case shape the Flow SDK expects (i.e. checkout.com's
 * Create-a-Payment-Session API response).
 *
 * ⚠️ Key names to be verified against the checkout.com sandbox — see the
 * migration plan, "Field mapping note".
 */
export const toFlowPaymentSession = (
  response: PaymentSessionResult,
): FlowPaymentSession => {
  const links: Record<string, FlowLinkInfo> = {}
  Object.entries(response.links ?? {}).forEach(([key, value]) => {
    if (value) {
      links[key] = {
        href: value.href,
        title: value.title,
        link: value.link,
      }
    }
  })

  return {
    id: response.id,
    payment_session_token: response.sessionToken,
    payment_session_secret: response.sessionSecret,
    _links: links,
  }
}

export interface CheckoutFlowInitConfig {
  paymentSession: FlowPaymentSession
  onReady?: () => void
  onChange?: (isValid: boolean) => void
  onPaymentCompleted: (paymentId: string) => void
  onError: (error: FlowComponentError) => void
}

export interface ICheckoutFlowService {
  init(config: CheckoutFlowInitConfig): Promise<void>
  mountFlow(element: string | HTMLElement): Promise<FlowComponent>
  unmount(flow?: FlowComponent): void
  isCheckoutReady(): boolean
}

class CheckoutFlowServiceClass implements ICheckoutFlowService {
  private status: string = ServiceStatus.NOT_INITIALIZED
  private checkout: CheckoutWebComponentsInstance | null = null

  public async init(config: CheckoutFlowInitConfig) {
    try {
      const checkoutWebComponents = await this.ensureIsReady()

      const colors = useThemeHelper().colors.value
      const fontFamily = "'Founders Grotesk', Arial, Helvetica, sans-serif"

      this.checkout = await checkoutWebComponents({
        publicKey: import.meta.env.SO_CHECKOUT_COM_API_KEY,
        environment: isProduction() ? "production" : "sandbox",
        locale: "en-GB",
        paymentSession: config.paymentSession,
        appearance: {
          colorAction: colors.success,
          colorInverse: colors["on-success"],
          colorPrimary: colors.inverted,
          colorSecondary: colors["night-light-1"],
          colorBackground: colors.surface,
          colorBorder: colors.border,
          colorFormBorder: colors.border,
          colorOutline: colors.focus,
          colorError: colors.error,
          colorDisabled: colors.disabled,
          colorFormBackground: colors.surface,
          focusOutlineWidth: "2px",
          focusOutlineOffset: "0px",
          borderRadius: ["4px", "4px"],
          label: { fontFamily, fontSize: "1.125rem", fontWeight: 400 },
          footnote: { fontFamily },
          subheading: { fontFamily },
          button: { fontFamily },
        },
        onReady: () => config.onReady?.(),
        onChange: (component) => config.onChange?.(component.isValid()),
        onPaymentCompleted: (_component, paymentResponse) =>
          config.onPaymentCompleted(paymentResponse.id),
        onError: (_component, error) => config.onError(error),
      })
    } catch (error) {
      this.status = ServiceStatus.ERROR
      throw error
    }
  }

  async mountFlow(element: string | HTMLElement) {
    if (!this.checkout) {
      throw new Error(
        "CheckoutFlowService.init must be called before mountFlow",
      )
    }
    // Mount the standalone "card" component rather than the full "flow" to not show Apple Pay / Google Pay buttons
    const card = this.checkout.create("card")

    if (!(await card.isAvailable())) {
      throw new Error("Card payment is not available")
    }

    return card.mount(element)
  }

  unmount(flow?: FlowComponent) {
    flow?.unmount()
    this.checkout = null
  }

  isCheckoutReady() {
    return this.status === ServiceStatus.READY
  }

  private async ensureIsReady(): Promise<CheckoutWebComponentsFunction> {
    if (
      this.status === ServiceStatus.NOT_INITIALIZED ||
      !globalThis.CheckoutWebComponents
    ) {
      this.status = ServiceStatus.INITIALIZED
      await loadScript(CHECKOUT_FLOW_SCRIPT_URL)
    }

    if (globalThis.CheckoutWebComponents) {
      this.status = ServiceStatus.READY
      return globalThis.CheckoutWebComponents
    }

    this.status = ServiceStatus.ERROR
    throw new Error("Checkout.com Flow script failed to load")
  }
}

export const CheckoutFlowService = new CheckoutFlowServiceClass()
